import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Search } from 'lucide-react';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import LoginSwitchTargets, { startLoginSwitch } from './LoginSwitchTargets';

// The Login Switcher screen (issue #283), Admin only: find a Customer by
// name or one of its login emails, then open the customer portal as one of
// its logins in a new tab. Production switches are read-only; dev ones can
// do everything the login can. The Customer record's View as Customer does
// the same for one Customer.

const SEARCH_DELAY_MS = 300;
const CARD_STYLE = { background: '#fff', border: '1px solid #eceef1', borderRadius: 10, padding: '12px 16px', marginBottom: 12 };

function LoginSwitcherPage() {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [search, setSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const term = search.trim();
    if (!term) {
      setResults(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get('/api/login-switcher/search', { params: { q: term } });
        if (!cancelled) setResults(res.data.data);
      } catch (err) {
        if (!cancelled) showError(err, 'Could not search Customers');
      }
      if (!cancelled) setLoading(false);
    }, SEARCH_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const switchTo = async (customer, loginId) => {
    setBusy(true);
    try {
      await startLoginSwitch(customer.id, loginId);
      dismissError();
    } catch (err) {
      showError(err, 'Could not start the login switch');
    }
    setBusy(false);
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Login Switcher</h2>
      </div>
      <div style={{ margin: '8px 24px 0', fontSize: 13, color: '#5a6b85' }}>
        Open the customer portal in a new tab as one of a Customer's logins, to see exactly what they see.
        {process.env.NODE_ENV === 'production'
          ? ' In production it\'s read-only: nothing can be saved, changed or sent.'
          : ' In development you can do everything the login can; emails go to the Test Email Address.'}
        {' '}A switch ends after 60 minutes, or 15 minutes without activity.
      </div>

      <div style={{ margin: '16px 24px 0' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#fff', borderRadius: 8, maxWidth: 360,
          border: `1px solid ${searchFocused ? '#1976d2' : '#d0d5dd'}`,
          boxShadow: searchFocused ? '0 0 0 2px rgba(25, 118, 210, 0.15)' : 'none',
        }}>
          <Search size={14} strokeWidth={1.75} color="#888" />
          <input
            type="text"
            autoFocus
            placeholder="Search Customer name or login email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            style={{ border: 'none', outline: 'none', fontSize: 14, width: '100%' }}
          />
        </div>
      </div>

      <div style={{ margin: '16px 24px 0', maxWidth: 720 }}>
        {loading && !results && <div style={{ fontSize: 13, color: '#5a6b85' }}>Searching...</div>}
        {results && results.length === 0 && <div style={{ fontSize: 13, color: '#999' }}>No Customers match.</div>}
        {results && results.map((customer) => (
          <div key={customer.id} style={CARD_STYLE}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{customer.name}</div>
            {customer.paused_reason && customer.logins.length > 0 && (
              <div style={{ fontSize: 12, color: '#8a5a00', marginBottom: 4 }}>Logins are paused: {customer.paused_reason}</div>
            )}
            <LoginSwitchTargets customer={customer} busy={busy} onSwitch={switchTo} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default LoginSwitcherPage;
